export type ChartIndicatorCategory =
  | 'trend'
  | 'momentum'
  | 'volatility'
  | 'volume'
  | 'derivatives'

export type ChartIndicatorPlacement = 'price' | 'volume' | 'pane'
export type ChartIndicatorSeriesStyle = 'line' | 'histogram'

export type IndicatorQueryRequest = {
  id: string
  params: Record<string, number>
}

export type ChartIndicatorOutputSeries = {
  outputId: string
  key: string
  label: string
  color: string
  style: ChartIndicatorSeriesStyle
}

export type ChartIndicatorReferenceLine = {
  value: number
  label?: string
  color?: string
  style?: 'solid' | 'dashed'
}

export type ChartIndicatorSpec = {
  id: string
  label: string
  category: ChartIndicatorCategory
  placement: ChartIndicatorPlacement
  outputs: readonly ChartIndicatorOutputSeries[]
  request: IndicatorQueryRequest
  fixedDomain?: readonly [number, number]
  referenceLines?: readonly ChartIndicatorReferenceLine[]
}

export const CHART_INDICATOR_REGISTRY = [
  {
    id: 'sma20',
    label: 'SMA 20 简单移动平均线',
    category: 'trend',
    placement: 'price',
    outputs: [{ outputId: 'value', key: 'sma20', label: 'SMA 20', color: '#38bdf8', style: 'line' }],
    request: { id: 'sma', params: { period: 20 } }
  },
  {
    id: 'ema20',
    label: 'EMA 20 指数移动平均线',
    category: 'trend',
    placement: 'price',
    outputs: [{ outputId: 'value', key: 'ema20', label: 'EMA 20', color: '#f59e0b', style: 'line' }],
    request: { id: 'ema', params: { period: 20 } }
  },
  {
    id: 'ema50',
    label: 'EMA 50 指数移动平均线',
    category: 'trend',
    placement: 'price',
    outputs: [{ outputId: 'value', key: 'ema50', label: 'EMA 50', color: '#a78bfa', style: 'line' }],
    request: { id: 'ema', params: { period: 50 } }
  },
  {
    id: 'wma20',
    label: 'WMA 20 加权移动平均线',
    category: 'trend',
    placement: 'price',
    outputs: [{ outputId: 'value', key: 'wma20', label: 'WMA 20', color: '#2dd4bf', style: 'line' }],
    request: { id: 'wma', params: { period: 20 } }
  },
  {
    id: 'adx14',
    label: 'ADX 14 平均趋向指数',
    category: 'trend',
    placement: 'pane',
    outputs: [
      { outputId: 'adx', key: 'adx14', label: 'ADX', color: '#fbbf24', style: 'line' },
      { outputId: 'plus_di', key: 'plus_di14', label: '+DI', color: '#22c55e', style: 'line' },
      { outputId: 'minus_di', key: 'minus_di14', label: '-DI', color: '#ef4444', style: 'line' }
    ],
    request: { id: 'adx', params: { period: 14 } },
    fixedDomain: [0, 100],
    referenceLines: [
      { value: 20, color: '#64748b', style: 'dashed' },
      { value: 25, label: '趋势阈值', color: '#64748b', style: 'dashed' }
    ]
  },
  {
    id: 'macd',
    label: 'MACD 指数平滑异同移动平均线',
    category: 'momentum',
    placement: 'pane',
    outputs: [
      { outputId: 'macd', key: 'macd', label: 'MACD', color: '#38bdf8', style: 'line' },
      { outputId: 'signal', key: 'macd_signal', label: '信号线', color: '#f59e0b', style: 'line' },
      { outputId: 'histogram', key: 'macd_histogram', label: '柱状差', color: '#94a3b8', style: 'histogram' }
    ],
    request: { id: 'macd', params: { fast: 12, slow: 26, signal: 9 } },
    referenceLines: [{ value: 0, color: '#64748b', style: 'dashed' }]
  },
  {
    id: 'rsi14',
    label: 'RSI 14 相对强弱指数',
    category: 'momentum',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'rsi14', label: 'RSI 14', color: '#c084fc', style: 'line' }],
    request: { id: 'rsi', params: { period: 14 } },
    fixedDomain: [0, 100],
    referenceLines: [
      { value: 30, label: '超卖', color: '#22c55e', style: 'dashed' },
      { value: 50, color: '#64748b', style: 'dashed' },
      { value: 70, label: '超买', color: '#ef4444', style: 'dashed' }
    ]
  },
  {
    id: 'roc12',
    label: 'ROC 12 变动率',
    category: 'momentum',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'roc12', label: 'ROC 12', color: '#22d3ee', style: 'line' }],
    request: { id: 'roc', params: { period: 12 } },
    referenceLines: [{ value: 0, color: '#64748b', style: 'dashed' }]
  },
  {
    id: 'williams_r14',
    label: 'Williams %R 14 威廉指标',
    category: 'momentum',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'williams_r14', label: 'Williams %R', color: '#fb7185', style: 'line' }],
    request: { id: 'williams_r', params: { period: 14 } },
    fixedDomain: [-100, 0],
    referenceLines: [
      { value: -80, label: '超卖', color: '#22c55e', style: 'dashed' },
      { value: -20, label: '超买', color: '#ef4444', style: 'dashed' }
    ]
  },
  {
    id: 'stochastic14_3',
    label: 'Stochastic 14/3 随机指标',
    category: 'momentum',
    placement: 'pane',
    outputs: [
      { outputId: 'k', key: 'stoch_k', label: '%K', color: '#38bdf8', style: 'line' },
      { outputId: 'd', key: 'stoch_d', label: '%D', color: '#f59e0b', style: 'line' }
    ],
    request: { id: 'stochastic', params: { k_period: 14, d_period: 3 } },
    fixedDomain: [0, 100],
    referenceLines: [
      { value: 20, label: '超卖', color: '#22c55e', style: 'dashed' },
      { value: 50, color: '#64748b', style: 'dashed' },
      { value: 80, label: '超买', color: '#ef4444', style: 'dashed' }
    ]
  },
  {
    id: 'cci20',
    label: 'CCI 20 顺势指标',
    category: 'momentum',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'cci20', label: 'CCI 20', color: '#f472b6', style: 'line' }],
    request: { id: 'cci', params: { period: 20 } },
    referenceLines: [
      { value: -100, color: '#22c55e', style: 'dashed' },
      { value: 0, color: '#64748b', style: 'dashed' },
      { value: 100, color: '#ef4444', style: 'dashed' }
    ]
  },
  {
    id: 'atr14',
    label: 'ATR 14 真实波幅',
    category: 'volatility',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'atr14', label: 'ATR 14', color: '#fb923c', style: 'line' }],
    request: { id: 'atr', params: { period: 14 } }
  },
  {
    id: 'bollinger20_2',
    label: '布林带 20/2',
    category: 'volatility',
    placement: 'price',
    outputs: [
      { outputId: 'upper', key: 'bb_upper', label: '布林上轨', color: '#818cf8', style: 'line' },
      { outputId: 'lower', key: 'bb_lower', label: '布林下轨', color: '#818cf8', style: 'line' }
    ],
    request: { id: 'bollinger', params: { period: 20, std: 2 } }
  },
  {
    id: 'donchian20',
    label: '唐奇安通道 20',
    category: 'volatility',
    placement: 'price',
    outputs: [
      { outputId: 'upper', key: 'donchian_high20', label: '唐奇安上轨', color: '#34d399', style: 'line' },
      { outputId: 'lower', key: 'donchian_low20', label: '唐奇安下轨', color: '#34d399', style: 'line' }
    ],
    request: { id: 'donchian', params: { period: 20 } }
  },
  {
    id: 'historical_volatility20_365',
    label: '历史波动率 20（365 天年化）',
    category: 'volatility',
    placement: 'pane',
    outputs: [
      { outputId: 'value', key: 'historical_volatility20', label: '历史波动率', color: '#f97316', style: 'line' }
    ],
    request: { id: 'historical_volatility', params: { period: 20, annualization_days: 365 } }
  },
  {
    id: 'keltner20_10_2',
    label: 'Keltner 通道 20/10/2',
    category: 'volatility',
    placement: 'price',
    outputs: [
      { outputId: 'middle', key: 'keltner_middle', label: 'Keltner 中轨', color: '#eab308', style: 'line' },
      { outputId: 'upper', key: 'keltner_upper', label: 'Keltner 上轨', color: '#facc15', style: 'line' },
      { outputId: 'lower', key: 'keltner_lower', label: 'Keltner 下轨', color: '#facc15', style: 'line' }
    ],
    request: { id: 'keltner', params: { ema_period: 20, atr_period: 10, multiplier: 2 } }
  },
  {
    id: 'vwap20',
    label: 'VWAP 20 成交量加权均价',
    category: 'volume',
    placement: 'price',
    outputs: [{ outputId: 'value', key: 'vwap20', label: 'VWAP 20', color: '#06b6d4', style: 'line' }],
    request: { id: 'vwap', params: { period: 20 } }
  },
  {
    id: 'volume_sma20',
    label: '成交量均线 20',
    category: 'volume',
    placement: 'volume',
    outputs: [
      { outputId: 'value', key: 'volume_sma20', label: '成交量均线 20', color: '#f59e0b', style: 'line' }
    ],
    request: { id: 'volume_sma', params: { period: 20 } }
  },
  {
    id: 'obv',
    label: 'OBV 能量潮',
    category: 'volume',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'obv', label: 'OBV', color: '#22c55e', style: 'line' }],
    request: { id: 'obv', params: {} },
    referenceLines: [{ value: 0, color: '#64748b', style: 'dashed' }]
  },
  {
    id: 'mfi14',
    label: 'MFI 14 资金流量指数',
    category: 'volume',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'mfi14', label: 'MFI 14', color: '#10b981', style: 'line' }],
    request: { id: 'mfi', params: { period: 14 } },
    fixedDomain: [0, 100],
    referenceLines: [
      { value: 20, label: '超卖', color: '#22c55e', style: 'dashed' },
      { value: 80, label: '超买', color: '#ef4444', style: 'dashed' }
    ]
  },
  {
    id: 'cmf20',
    label: 'CMF 20 Chaikin 资金流',
    category: 'volume',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'cmf20', label: 'CMF 20', color: '#14b8a6', style: 'line' }],
    request: { id: 'cmf', params: { period: 20 } },
    fixedDomain: [-1, 1],
    referenceLines: [{ value: 0, color: '#64748b', style: 'dashed' }]
  },
  {
    id: 'funding_rate',
    label: '资金费率',
    category: 'derivatives',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'funding_rate', label: '资金费率', color: '#f472b6', style: 'histogram' }],
    request: { id: 'funding_rate', params: {} },
    referenceLines: [{ value: 0, color: '#64748b', style: 'dashed' }]
  },
  {
    id: 'open_interest',
    label: '未平仓量',
    category: 'derivatives',
    placement: 'pane',
    outputs: [{ outputId: 'value', key: 'open_interest', label: '未平仓量', color: '#60a5fa', style: 'line' }],
    request: { id: 'open_interest', params: {} }
  }
] as const satisfies readonly ChartIndicatorSpec[]

export type ChartIndicatorId = typeof CHART_INDICATOR_REGISTRY[number]['id']
export type ChartIndicatorParams = Partial<Record<ChartIndicatorId, Record<string, number>>>
export type ChartIndicatorPresetId =
  | 'price'
  | 'trend'
  | 'momentum'
  | 'volatility'
  | 'volume'
  | 'derivatives'

export const CHART_INDICATOR_IDS = CHART_INDICATOR_REGISTRY.map(
  indicator => indicator.id
) as ChartIndicatorId[]

export const DEFAULT_TREND_INDICATORS = ['ema20', 'ema50'] as const satisfies readonly ChartIndicatorId[]

export const CHART_INDICATOR_PRESETS = {
  price: [],
  trend: DEFAULT_TREND_INDICATORS,
  momentum: ['rsi14', 'macd'],
  volatility: ['bollinger20_2', 'atr14'],
  volume: ['volume_sma20', 'obv'],
  derivatives: ['funding_rate', 'open_interest']
} as const satisfies Record<ChartIndicatorPresetId, readonly ChartIndicatorId[]>

const indicatorById = new Map<ChartIndicatorId, typeof CHART_INDICATOR_REGISTRY[number]>(
  CHART_INDICATOR_REGISTRY.map(indicator => [indicator.id, indicator])
)
const indicatorIds = new Set<string>(CHART_INDICATOR_IDS)
const REQUIRED_CARD_INDICATORS = ['rsi14', 'volume_sma20'] as const satisfies readonly ChartIndicatorId[]

export type ChartIndicatorRequestPlanSlot = {
  slotId: ChartIndicatorId
  requestKey: string
  spec: ChartIndicatorSpec
}

export type ChartIndicatorRequestPlan = {
  requests: IndicatorQueryRequest[]
  slots: ChartIndicatorRequestPlanSlot[]
  fingerprint: string
}

export type IndicatorSeriesBindingDefinition = {
  id: string
  params: Record<string, number>
  series_keys: readonly string[]
  outputs: readonly {
    output_id: string
    series_key: string
  }[]
}

export type IndicatorSeriesBindingSource = {
  series: Record<string, Array<number | null>>
  definitions: readonly IndicatorSeriesBindingDefinition[]
}

export type ChartIndicatorSeriesBinding = {
  series: Record<string, Array<number | null>>
  warnings: string[]
}

export function normalizeChartIndicatorSelection(value: unknown): ChartIndicatorId[] {
  if (!Array.isArray(value)) return []
  const normalized: ChartIndicatorId[] = []
  const seen = new Set<ChartIndicatorId>()
  for (const candidate of value) {
    if (typeof candidate !== 'string' || !indicatorIds.has(candidate)) continue
    const id = candidate as ChartIndicatorId
    if (seen.has(id)) continue
    seen.add(id)
    normalized.push(id)
    if (seen.size === CHART_INDICATOR_IDS.length) break
  }
  return normalized
}

export function normalizeChartIndicatorParams(value: unknown): ChartIndicatorParams {
  if (!isRecord(value)) return {}
  const normalized: ChartIndicatorParams = {}
  for (const id of CHART_INDICATOR_IDS) {
    const candidate = value[id]
    if (!isRecord(candidate)) continue
    const params: Record<string, number> = {}
    for (const [name, paramValue] of Object.entries(candidate)) {
      if (typeof paramValue !== 'number' || !Number.isFinite(paramValue)) continue
      params[name] = paramValue
    }
    if (Object.keys(params).length) normalized[id] = params
  }
  return normalized
}

export function effectiveChartIndicatorParams(
  id: ChartIndicatorId,
  indicatorParams: unknown = undefined
): Record<string, number> {
  const indicator = indicatorById.get(id)
  if (!indicator) return {}
  const overrides = normalizeChartIndicatorParams(indicatorParams)[id] ?? {}
  return { ...indicator.request.params, ...overrides }
}

export function configuredChartIndicatorSpec(
  id: ChartIndicatorId,
  indicatorParams: unknown = undefined
): ChartIndicatorSpec {
  const indicator = indicatorById.get(id)
  if (!indicator) throw new Error(`Unknown chart indicator slot: ${id}`)
  const params = effectiveChartIndicatorParams(id, indicatorParams)
  const customized = requestParamsKey(params) !== requestParamsKey(indicator.request.params)
  if (!customized) {
    return {
      ...indicator,
      outputs: indicator.outputs.map(output => ({ ...output })),
      request: { id: indicator.request.id, params }
    }
  }
  const methodLabel = indicator.request.id.replace(/_/g, ' ').toUpperCase()
  const paramsLabel = Object.entries(params)
    .sort(([left], [right]) => compareText(left, right))
    .map(([name, value]) => `${name}=${value}`)
    .join(', ')
  return {
    ...indicator,
    label: `${methodLabel} · ${paramsLabel}`,
    outputs: indicator.outputs.map(output => ({
      ...output,
      label: indicator.outputs.length === 1
        ? `${methodLabel} (${paramsLabel})`
        : `${output.label} (${paramsLabel})`
    })),
    request: { id: indicator.request.id, params }
  }
}

export function chartIndicatorConfigFingerprint(
  selection: unknown,
  indicatorParams: unknown = undefined
): string {
  const selected = normalizeChartIndicatorSelection(selection)
  const normalizedParams = normalizeChartIndicatorParams(indicatorParams)
  return stableJson({
    indicators: selected,
    slots: CHART_INDICATOR_IDS.map(slotId => ({
      slotId,
      request: {
        id: indicatorById.get(slotId)?.request.id ?? '',
        params: effectiveChartIndicatorParams(slotId, normalizedParams)
      }
    }))
  })
}

export function buildChartIndicatorRequestPlan(
  selection: unknown,
  indicatorParams: unknown = undefined
): ChartIndicatorRequestPlan {
  const selected = normalizeChartIndicatorSelection(selection)
  const normalizedParams = normalizeChartIndicatorParams(indicatorParams)
  const requestedIds = [
    ...selected,
    ...REQUIRED_CARD_INDICATORS.filter(id => !selected.includes(id))
  ]
  const requests: IndicatorQueryRequest[] = []
  const requestKeys = new Set<string>()
  const slots: ChartIndicatorRequestPlanSlot[] = []

  for (const id of requestedIds) {
    const spec = configuredChartIndicatorSpec(id, normalizedParams)
    const requestKey = indicatorRequestKey(spec.request)
    slots.push({ slotId: id, requestKey, spec })
    if (!requestKeys.has(requestKey)) {
      requestKeys.add(requestKey)
      requests.push({ id: spec.request.id, params: { ...spec.request.params } })
    }
  }

  return {
    requests,
    slots,
    fingerprint: chartIndicatorConfigFingerprint(selected, normalizedParams)
  }
}

export function buildChartIndicatorRequests(
  selection: unknown,
  indicatorParams: unknown = undefined
): IndicatorQueryRequest[] {
  return buildChartIndicatorRequestPlan(selection, indicatorParams).requests
}

export function bindChartIndicatorSeries(
  plan: ChartIndicatorRequestPlan,
  response: IndicatorSeriesBindingSource
): ChartIndicatorSeriesBinding {
  const series: Record<string, Array<number | null>> = {}
  const warnings: string[] = []
  const definitionsByRequest = new Map<string, IndicatorSeriesBindingDefinition[]>()

  for (const definition of response.definitions ?? []) {
    if (!definition || typeof definition.id !== 'string' || !isRecord(definition.params)) continue
    const key = indicatorRequestKey({
      id: definition.id,
      params: definition.params
    })
    const matches = definitionsByRequest.get(key) ?? []
    matches.push(definition)
    definitionsByRequest.set(key, matches)
  }

  for (const slot of plan.slots) {
    const definitions = definitionsByRequest.get(slot.requestKey) ?? []
    if (definitions.length === 0) {
      warnings.push(`指标 ${slot.slotId} 缺少响应定义`)
      continue
    }
    if (definitions.length > 1) {
      warnings.push(`指标 ${slot.slotId} 存在重复响应定义`)
      continue
    }
    const definition = definitions[0]
    const definitionOutputs = Array.isArray(definition.outputs) ? definition.outputs : []
    const definitionSeriesKeys = Array.isArray(definition.series_keys) ? definition.series_keys : []

    for (const output of slot.spec.outputs) {
      const outputBindings = definitionOutputs.filter(binding => binding?.output_id === output.outputId)
      if (outputBindings.length !== 1) {
        warnings.push(`指标 ${slot.slotId} 输出 ${output.outputId} 缺少或重复绑定`)
        continue
      }
      const sourceKey = outputBindings[0].series_key
      const keyListedOnce = typeof sourceKey === 'string'
        && definitionSeriesKeys.filter(key => key === sourceKey).length === 1
      const keyBoundOnce = typeof sourceKey === 'string'
        && definitionOutputs.filter(binding => binding?.series_key === sourceKey).length === 1
      if (!keyListedOnce || !keyBoundOnce) {
        warnings.push(`指标 ${slot.slotId} 输出 ${output.outputId} 的 series_key 绑定错配`)
        continue
      }
      const values = response.series?.[sourceKey]
      if (!Array.isArray(values)) {
        warnings.push(`指标 ${slot.slotId} 输出 ${output.outputId} 缺少序列 ${sourceKey}`)
        continue
      }
      series[output.key] = values
    }
  }

  return { series, warnings }
}

export function priceIndicatorSpecs(
  selection: unknown,
  indicatorParams: unknown = undefined
): ChartIndicatorSpec[] {
  return indicatorSpecsByPlacement(selection, 'price', indicatorParams)
}

export function volumeIndicatorSpecs(
  selection: unknown,
  indicatorParams: unknown = undefined
): ChartIndicatorSpec[] {
  return indicatorSpecsByPlacement(selection, 'volume', indicatorParams)
}

export function paneIndicatorSpecs(
  selection: unknown,
  indicatorParams: unknown = undefined
): ChartIndicatorSpec[] {
  return indicatorSpecsByPlacement(selection, 'pane', indicatorParams)
}

export function selectedChartIndicatorSpecs(
  selection: unknown,
  indicatorParams: unknown = undefined
): ChartIndicatorSpec[] {
  const normalizedParams = normalizeChartIndicatorParams(indicatorParams)
  return normalizeChartIndicatorSelection(selection)
    .map(id => configuredChartIndicatorSpec(id, normalizedParams))
}

export function defaultChartIndicatorForMethod(methodId: string): ChartIndicatorId | null {
  return CHART_INDICATOR_REGISTRY.find(indicator => indicator.request.id === methodId)?.id ?? null
}

function indicatorSpecsByPlacement(
  selection: unknown,
  placement: ChartIndicatorPlacement,
  indicatorParams: unknown
): ChartIndicatorSpec[] {
  return selectedChartIndicatorSpecs(selection, indicatorParams)
    .filter(indicator => indicator.placement === placement)
}

function indicatorRequestKey(request: IndicatorQueryRequest): string {
  return `${request.id}:${requestParamsKey(request.params)}`
}

function requestParamsKey(params: Record<string, number>): string {
  return stableJson(params)
}

function stableJson(value: unknown): string {
  return JSON.stringify(canonicalValue(value))
}

function canonicalValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalValue)
  if (!isRecord(value)) return value
  return Object.fromEntries(
    Object.keys(value)
      .sort(compareText)
      .map(key => [key, canonicalValue(value[key])])
  )
}

function compareText(left: string, right: string): number {
  if (left < right) return -1
  if (left > right) return 1
  return 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
